import { PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../../components/Autocomplete";
import { FormId } from "../../../../components/enums";
import { defaultCountry } from "../../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../../store/HOC/genericUI.slice";
import { generateFormattedDate } from "../../../../utilities/functions";

import { ComponentId as CircumstancesComponentId } from "./CircumstancesData/enums";
import { ComponentId as OtherPersonsComponentId } from "./OtherPersonsData/enums";
import { ComponentId as StatementsComponentId } from "./StatementsData/enums";

export type FormInput = {
  [FormId.INCIDENT_CIRCUMSTANCES]: {
    [CircumstancesComponentId.DATE]: string;
    [CircumstancesComponentId.HOUR]: string;
    [CircumstancesComponentId.PLACE]: string;
    [CircumstancesComponentId.CITY]: string;
    [CircumstancesComponentId.ZIP_CODE_PL]: string;
    [CircumstancesComponentId.ZIP_CODE_GENERIC]: string;
    [CircumstancesComponentId.COUNTRY]: AutocompleteItem;
    [CircumstancesComponentId.PROVINCE]: string;
    [CircumstancesComponentId.DESCRIPTION]: string;
  };
  [FormId.INCIDENT_STATEMENTS]: {
    [StatementsComponentId.TYPE]: string;
    [StatementsComponentId.POLICE_DEPARTMENT]: string;
    [StatementsComponentId.CASE_NUMBER]: string;
  };
  [FormId.INCIDENT_OTHER_PERSONS]: {
    [OtherPersonsComponentId.ARE_OTHER_PEOPLE_INVOLVED]: string;
    [OtherPersonsComponentId.PERSONS]: {
      [key: string]: { [key: string]: string };
    };
  };
};

export type IncidentCircumstancesFormInputs = FormInput[FormId.INCIDENT_CIRCUMSTANCES];
export type IncidentOtherPersonsFormInputs = FormInput[FormId.INCIDENT_OTHER_PERSONS];
export type IncidentStatementsFormInputs = FormInput[FormId.INCIDENT_STATEMENTS];

type IncidentState = {
  data: FormInput;
  ui: UIState;
};

const initialState: IncidentState = {
  data: {
    [FormId.INCIDENT_CIRCUMSTANCES]: {
      [CircumstancesComponentId.DATE]: generateFormattedDate(),
      [CircumstancesComponentId.HOUR]: "",
      [CircumstancesComponentId.PLACE]: "",
      [CircumstancesComponentId.CITY]: "",
      [CircumstancesComponentId.ZIP_CODE_PL]: "",
      [CircumstancesComponentId.ZIP_CODE_GENERIC]: "",
      [CircumstancesComponentId.COUNTRY]: {
        ...defaultCountry,
      },
      [CircumstancesComponentId.PROVINCE]: "",
      [CircumstancesComponentId.DESCRIPTION]: "",
    },
    [FormId.INCIDENT_STATEMENTS]: {
      [StatementsComponentId.TYPE]: "",
      [StatementsComponentId.POLICE_DEPARTMENT]: "",
      [StatementsComponentId.CASE_NUMBER]: "",
    },
    [FormId.INCIDENT_OTHER_PERSONS]: {
      [OtherPersonsComponentId.ARE_OTHER_PEOPLE_INVOLVED]: "",
      [OtherPersonsComponentId.PERSONS]: {},
    },
  },
  ui: {
    validated: false,
    "circumstances[country]": defaultCountry.value,
  },
};

const incidentSlice = createGenericUISlice({
  name: "@OC/Property/incident",
  initialState: initialState as GenericState<FormInput>,
  reducers: {
    setData: (state, action: PayloadAction<FormInput>) => {
      state.data[FormId.INCIDENT_CIRCUMSTANCES] = {
        ...initialState.data[FormId.INCIDENT_CIRCUMSTANCES],
        ...action.payload[FormId.INCIDENT_CIRCUMSTANCES],
      };
      state.data[FormId.INCIDENT_STATEMENTS] = {
        ...initialState.data[FormId.INCIDENT_STATEMENTS],
        ...action.payload[FormId.INCIDENT_STATEMENTS],
      };
      state.data[FormId.INCIDENT_OTHER_PERSONS] = {
        ...initialState.data[FormId.INCIDENT_OTHER_PERSONS],
        ...action.payload[FormId.INCIDENT_OTHER_PERSONS],
      };
      state.ui.validated = true;
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["circumAvailableCities"] = action.payload;
    },
    setStatementType: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_STATEMENTS][StatementsComponentId.TYPE] =
        action.payload;
    },
    setPersonsAreInvolved: (state, action: PayloadAction<string>) => {
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.ARE_OTHER_PEOPLE_INVOLVED
      ] = action.payload;
      state.data[FormId.INCIDENT_OTHER_PERSONS][
        OtherPersonsComponentId.PERSONS
      ] =
        initialState.data[FormId.INCIDENT_OTHER_PERSONS][
          OtherPersonsComponentId.PERSONS
        ];
    },
  },
});

export const {
  setData: setPropertyIncidentFormData,
  setPersonsAreInvolved,
  setStatementType,
  setUIField,
  setUIObjectField,
  setAvailableCities,
} = incidentSlice.actions;

export const { reducer } = incidentSlice;
